import React from 'react';
import * as Sentry from '@sentry/react';

const RELOAD_STAMP_KEY = 'porter:chunk-reload-at';
const RELOAD_COOLDOWN_MS = 60_000;

export function isChunkLoadError(error: unknown) {
  const { name, message } = (error ?? {}) as {
    name?: string;
    message?: string;
  };
  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk .+ failed|Unexpected token '<'|Failed to fetch dynamically imported module/i.test(
      message ?? '',
    )
  );
}

function lastReloadAt() {
  try {
    return Number(sessionStorage.getItem(RELOAD_STAMP_KEY)) || 0;
  } catch {
    return 0;
  }
}

function stampReload() {
  try {
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
  } catch {
    // storage unavailable: the cooldown simply won't apply
  }
}

/**
 * A tab opened before a deploy asks for chunk hashes that no longer exist.
 * Reload once so it picks up the current build; a second failure inside the
 * cooldown is a real error and propagates to the error boundary.
 */
export function importWithReload<T>(
  factory: () => Promise<T>,
  reload: () => void = () => window.location.reload(),
): Promise<T> {
  return factory().catch((error: unknown) => {
    if (
      !isChunkLoadError(error) ||
      Date.now() - lastReloadAt() < RELOAD_COOLDOWN_MS
    ) {
      throw error;
    }
    stampReload();
    Sentry.captureMessage('Chunk load failed; reloading', {
      level: 'warning',
      fingerprint: ['chunk-load-reload'],
      extra: { message: (error as Error | undefined)?.message },
    });
    reload();
    // Keep the Suspense fallback up while the page reloads.
    return new Promise<T>(() => {});
  });
}

export default function lazyWithReload<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return React.lazy(() => importWithReload(factory));
}
